import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {null} */
  #variantTitleMap = null;

  /** Always use product title on PLP (not variant custom.title) */
  #getVariantTitleMap() {
    return {};
  }

  /**
   * @param {string | number | null | undefined} _variantId
   * @returns {string} product title
   */
  #labelForVariantId(_variantId) {
    return this.dataset.productTitle ?? '';
  }

  /** @param {string} text */
  #setTitleText(text) {
    if (!text) return;
    const el = this.querySelector('[data-plp-variant-title]');
    if (el) { el.textContent = text; return; }
    const heading =
      this.querySelector('a[ref="productTitleLink"] .text-block :is(h1,h2,h3,h4,h5,h6)') ??
      this.querySelector('product-title .title-text');
    if (heading) heading.textContent = text;
  }

  /** Label for the currently-checked (committed) swatch */
  #labelForCheckedSwatch() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checked ? this.#labelForVariantId(checked.dataset.variantId ?? null) : (this.dataset.productTitle ?? '');
  }

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    document.addEventListener('vsk:variant:changed', this.#onVskVariantChanged);
    this.addEventListener('click', this.#onSwatchKingClick, true);

    this.addEventListener('click', this.navigateToProduct);
    // Sync gallery immediately on swatch click (before async section fetch). Otherwise the
    // pointerenter preview stays visible until pointerleave runs debounced resetVariant.
    this.addEventListener('change', this.#onSwatchRadioChange, true);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('vsk:variant:changed', this.#onVskVariantChanged);
    this.removeEventListener('click', this.#onSwatchKingClick, true);
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('change', this.#onSwatchRadioChange, true);
  }

  /**
   * @param {string} selectedImageId - Featured media id for the chosen swatch (matches slide-id).
   */
  #applySlideshowForSelectedMediaId(selectedImageId) {
    const { slideshow } = this.refs;
    if (!slideshow || !selectedImageId) return;

    const id = String(selectedImageId);
    const { slides = [] } = slideshow.refs;

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== id;
    }

    slideshow.select({ id }, undefined, { animate: false });
    this.#previousSlideIndex = null;
  }

  /** Swatch row that last received input; fabric loop duplicates pickers so "first in DOM" is often wrong. */
  /** @type {HTMLElement | null} */
  #lastSwatchPickerContext = null;

  /** @param {Event} event */
  #onSwatchRadioChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio' || !t.checked) return;
    const picker = t.closest('swatches-variant-picker-component');
    if (!(picker instanceof HTMLElement) || !this.contains(picker)) return;
    const mediaId = t.dataset.optionMediaId;
    if (!mediaId) return;

    this.#lastSwatchPickerContext = picker;
    this.resetVariant.cancel();
    this.#applySlideshowForSelectedMediaId(mediaId);

    this.#setTitleText(this.#labelForVariantId(t.dataset.variantId ?? null));
  };

  /**
   * Picker that dispatched variant events (fabric row vs main swatches block = different elements).
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #swatchPickerDispatcherFromEvent(event) {
    const t = event.target;
    if (t instanceof HTMLElement && t.matches('swatches-variant-picker-component') && this.contains(t)) {
      return t;
    }
    return null;
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const dispatcher = this.#swatchPickerDispatcherFromEvent(event);
    const primary = this.variantPicker;
    const id = event.detail?.resource?.id;
    if (!id || !primary) return;

    // Keep the first (block) picker in sync when selection came from another instance (e.g. fabric row).
    if (dispatcher && dispatcher !== primary && this.contains(dispatcher)) {
      primary.updateSelectedOption?.(id);
    } else if (!dispatcher && event.target !== primary) {
      primary.updateSelectedOption?.(id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    const dispatcher = this.#swatchPickerDispatcherFromEvent(event);
    const dispatcherAny = /** @type {{ updateVariantPicker?: (html: Document) => void }} */ (dispatcher);
    if (dispatcher && typeof dispatcherAny.updateVariantPicker === 'function') {
      dispatcherAny.updateVariantPicker(event.detail.data.html);
    } else if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker?.(event.detail.data.html);
    }

    this.#updateVariantImages(dispatcher);
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    const variantId = event.detail?.resource?.id ?? this.getSelectedVariantId();
    requestAnimationFrame(() => this.#setTitleText(this.#labelForVariantId(variantId)));

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * @param {HTMLElement | null} [dispatcherPicker] - Picker that fired variant:update (when duplicated on the card).
   */
  #updateVariantImages(dispatcherPicker = null) {
    const picker =
      dispatcherPicker instanceof HTMLElement &&
      dispatcherPicker.matches('swatches-variant-picker-component') &&
      this.contains(dispatcherPicker)
        ? dispatcherPicker
        : this.variantPicker;

    const pickerAny = /** @type {{ selectedOption?: HTMLInputElement }} */ (picker);
    if (!pickerAny?.selectedOption) {
      return;
    }

    const selectedImageId = pickerAny.selectedOption.dataset.optionMediaId;
    if (selectedImageId) {
      this.#applySlideshowForSelectedMediaId(selectedImageId);
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The media id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });

    const input = this.querySelector(`input[data-option-media-id="${id}"][data-variant-id]`);
    if (input instanceof HTMLInputElement) {
      this.#setTitleText(this.#labelForVariantId(input.dataset.variantId ?? null));
    }
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }

    this.#setTitleText(this.#labelForCheckedSwatch());
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const picker =
      this.#lastSwatchPickerContext &&
      this.contains(this.#lastSwatchPickerContext) &&
      this.#lastSwatchPickerContext.isConnected
        ? this.#lastSwatchPickerContext
        : this.variantPicker;

    const pickerAny = /** @type {{ selectedOption?: HTMLInputElement }} */ (picker);
    // If we have a selected variant, always use its image
    if (pickerAny?.selectedOption) {
      const id = pickerAny.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /** @param {Event} e */
  #onVskVariantChanged = (e) => {
    if (!(e instanceof CustomEvent)) return;
    const detail = /** @type {Record<string, unknown>} */ (e.detail ?? {});
    const myId = this.dataset.productId;
    const nested = detail.product;
    const pid =
      detail.productId ??
      detail.product_id ??
      (nested && typeof nested === 'object' && nested !== null && 'id' in nested
        ? /** @type {{ id?: unknown }} */ (nested).id
        : undefined);
    if (pid != null && myId != null && String(pid) !== String(myId)) return;

    const variant = detail.variant;
    const vid =
      detail.variantID ??
      detail.variantId ??
      detail.variant_id ??
      (variant && typeof variant === 'object' && variant !== null && 'id' in variant
        ? /** @type {{ id?: unknown }} */ (variant).id
        : undefined);
    if (vid != null && (typeof vid === 'string' || typeof vid === 'number')) {
      this.#lastVskVariantId = String(vid);
      setTimeout(() => this.#setTitleText(this.#labelForVariantId(vid)), 50);
    }
  };

  /**
   * Set by #onVskVariantChanged so the click handler knows a variant ID was resolved.
   * @type {string | null}
   */
  #lastVskVariantId = null;

  /** @param {Event} e */
  #onSwatchKingClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (!t.closest('.swatch-view-item, .swatch-label, ul.swatch-view, variant-swatch-king')) return;
    if (!this.contains(t)) return;

    this.#lastVskVariantId = null;
    const optionGroup = t.closest('[option-target]');

    for (const delay of [80, 200, 400]) {
      setTimeout(() => {
        const vid = this.#resolveSwatchKingVariantId();
        if (vid) this.#setTitleText(this.#labelForVariantId(vid));

        const checkedSwatch = optionGroup?.querySelector('.swatch-view-item[aria-checked="true"]');
        if (checkedSwatch) this.#resolveAndApplySwatchKingVariant(checkedSwatch);
      }, delay);
    }
  };

  /** @type {{ variants: Array<Record<string, unknown>> } | null} */
  #productJsonCache = null;

  /** @returns {string | null} Product handle, read from the card's own link. */
  #getProductHandle() {
    try {
      const href = this.refs.productCardLink?.href;
      if (!href) return null;
      return new URL(href).pathname.split('/').filter(Boolean).pop() ?? null;
    } catch {
      return null;
    }
  }

  /** @returns {Promise<{ variants: Array<Record<string, unknown>> } | null>} */
  async #fetchProductJson() {
    if (this.#productJsonCache) return this.#productJsonCache;
    const handle = this.#getProductHandle();
    if (!handle) return null;
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      this.#productJsonCache = await response.json();
      return this.#productJsonCache;
    } catch {
      return null;
    }
  }

  /** @returns {string | null} The variant ID currently reflected in the card's link. */
  #currentVariantIdFromLink() {
    try {
      const href = this.refs.productCardLink?.href;
      return href ? new URL(href).searchParams.get('variant') : null;
    } catch {
      return null;
    }
  }

  /**
   * Swatch King only swaps the card's image/title when a swatch is clicked - it never updates
   * the card's link, so clicking through lands on whichever variant the card originally loaded
   * with. This resolves the real target variant from the swatch's own data (the option value
   * text it represents and which option position it controls) matched against the product's
   * actual variant list, keeping every other option the same as the currently selected variant.
   * @param {Element} checkedSwatch
   */
  async #resolveAndApplySwatchKingVariant(checkedSwatch) {
    const optionGroup = checkedSwatch.closest('[option-target]');
    const optionKey = optionGroup?.getAttribute('option-target');
    const newValue = checkedSwatch.getAttribute('orig-value');
    if (!optionKey || !newValue) return;

    const data = await this.#fetchProductJson();
    if (!data) return;

    const currentId = this.#currentVariantIdFromLink();
    const currentVariant = data.variants.find((v) => String(v.id) === String(currentId)) ?? data.variants[0];
    if (!currentVariant) return;

    const optionKeys = ['option1', 'option2', 'option3'];
    const match = data.variants.find(
      (v) => v[optionKey] === newValue && optionKeys.every((k) => k === optionKey || v[k] === currentVariant[k])
    );
    if (!match) return;

    this.#applyResolvedVariantLink(match);

    // Apply on every device: Swatch King's own click-driven image swap isn't reliable without a
    // real preceding hover (seemingly debounced), so a genuine click can leave the old photo
    // showing even though the swatch itself is marked selected. We take full ownership of the
    // image here instead of depending on Swatch King's own swap.
    this.#applyResolvedVariantImage(match);
  }

  /** @param {Record<string, unknown>} variant */
  #applyResolvedVariantLink(variant) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    try {
      const url = new URL(productCardLink.href);
      url.searchParams.set('variant', String(variant.id));
      const productUrl = url.toString();
      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
    } catch {
      /* ignore */
    }
  }

  /**
   * Swatch King's image swap on the card is tied to `switch_on_hover`, so on touch devices
   * (no hover) the swatch shows as selected but the photo never changes, and even on desktop a
   * genuine click without a preceding hover doesn't reliably trigger Swatch King's own swap. Set
   * the resolved variant's photo directly so it doesn't depend on Swatch King's swap at all.
   * Clears srcset first so the browser doesn't immediately override src with a different-size
   * fetch of the old image (the same race the theme already guards against elsewhere).
   * @param {{ featured_image?: { src?: string } }} variant
   */
  #applyResolvedVariantImage(variant) {
    const src = variant.featured_image?.src;
    if (typeof src !== 'string' || !src) return;

    const img = /** @type {HTMLImageElement | null} */ (
      this.querySelector('slideshow-slide:not([hidden]) img.product-media__image') ??
      this.querySelector('.card-gallery img.product-media__image')
    );
    if (!img) return;

    img.removeAttribute('srcset');
    img.removeAttribute('data-srcset');
    img.src = src;
  }

  /**
   * Attempts to read the active variant ID after a Swatch King interaction.
   * Checks multiple sources: the vsk:variant:changed event (captured earlier),
   * data-attributes on checked swatches, hidden inputs, and the card link URL.
   * @returns {string | null}
   */
  #resolveSwatchKingVariantId() {
    if (this.#lastVskVariantId) return this.#lastVskVariantId;

    const checked = this.querySelector('.swatch-view-item[aria-checked="true"]');
    const fromAttr = checked?.getAttribute('data-variant-id') ?? checked?.getAttribute('variant-id');
    if (fromAttr) return fromAttr;

    const hiddenInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"], input[name*="variant"][type="hidden"]')
    );
    if (hiddenInput?.value) return hiddenInput.value;

    try {
      const href = this.refs.productCardLink?.href;
      if (href) {
        const v = new URL(href).searchParams.get('variant');
        if (v) return v;
      }
    } catch { /* ignore */ }

    return null;
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}





// // custom script for product page 
// document.addEventListener('vsk:variant:changed', function(e) {
//   const variant = e.detail?.variant;
//   if (!variant) return;

//   console.log('Swatch King variant changed:', variant);

//   // --- Update Product Title ---
//   const titleEl = document.querySelector('.text-block h1');
//   if (titleEl) {
//     titleEl.textContent = variant.title;
//   }

//   // --- Update Product Price ---
//   const priceEl = document.querySelector('.product-price .price');
//   if (priceEl) {
//     const moneyFormat = window?.Shopify?.money_format || "${{amount}}";
//     const formattedPrice = Shopify.formatMoney(variant.price, moneyFormat);
//     priceEl.innerHTML = formattedPrice;
//   }
// }); 



